import lod_ from "lodash";
import { Autocomplete, Icon, InputAdornment } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDInput from "components/Basics/MDInput";
import { getLabel } from "helpers/dictionary";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import formAction from "redux-react/actions/formAction";

const InputString = ({ filter, page, language, updateStoreFilterDebounce, updateStoreFilter }) => {
	const dispatch = useDispatch();

	const [options, setOptions] = useState([]);

	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [currentSearchText, setCurrentSearchText] = useState("");

	/**
	 * Load suggestions with customizable display
	 * @param {string} searchText - The text to search for suggestions
	 * @param {number} limit - The maximum number of suggestions to return
	 * @param {number} page - The page number for pagination
	 * @returns {Promise<Object>} - An object containing results and a boolean indicating if more
	 */
	async function loadSuggestions(searchText, limit = 10, page = 1) {
		try {
			const result = await new Promise(resolve => {
				dispatch(
					formAction.getItemsFromCollection(
						filter.dictionary,
						{
							query: {
								[filter.attribute]: { $regex: searchText, $options: "i" }
							},
							limit,
							page,
							projection: {
								_id: 0,
								[filter.attribute]: 1
							},
							sort: {
								[filter.attribute]: 1 // Sort by the attribute in ascending order
							},
							unique: true
						},
						res => {
							resolve(res);
						}
					)
				);
			});

			const results = result?.items || [];
			const pagination = result?.pagination || {};
			const currentPage = pagination?.currentPage || 1;
			const totalPages = pagination?.totalPages || 1;

			const hasMore = results.length === limit && currentPage < totalPages; // If we got less than the limit, no more data

			return { results, hasMore };
		} catch (error) {
			return { results: [], hasMore: false };
		}
	}

	/**
	 * Search for suggestions based on the input value
	 * @param {string} val - The search text
	 * @param {number} requestPage - The page number for pagination
	 * @param {boolean} append - Whether to append results to the existing options
	 * @returns {Promise<void>} - A promise that resolves when the search is complete
	 */
	const search = async (val, requestPage = 1, append = false) => {
		updateStoreFilter(val, filter, page);
		setLoading(true);
		try {
			const { results, hasMore: moreAvailable } = await loadSuggestions(val, 10, requestPage);

			if (append) {
				setOptions(prevOptions => [...prevOptions, ...results]);
			} else {
				setOptions(results);
				setCurrentPage(1);
				setCurrentSearchText(val);
			}

			setHasMore(moreAvailable);
			setCurrentPage(requestPage);
		} catch (error) {
			//
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Handle changes in the Autocomplete component
	 * @param {Object} event - The event object from the Autocomplete
	 * @param {Object} newValue - The new value selected by the user
	 * @param {string} reason - The reason for the change (e.g., "selectOption", "clear")
	 * @returns {void} - Handles the change event for the Autocomplete component
	 */
	const onChangeAutocomplete = (event, newValue, reason) => {
		if (reason === "clear") {
			// If the user clears the input, we reset the current value
			search("", 1, false);
			setCurrentSearchText("");
			return;
		}
		setCurrentSearchText(newValue[filter.attribute] || "");
		updateStoreFilter(newValue[filter.attribute], filter, page);
	};

	/**
	 * Load more results when the user scrolls to the bottom of the listbox
	 */
	const loadMore = async () => {
		if (hasMore && !loading) {
			const nextPage = currentPage + 1;
			await search(currentSearchText, nextPage, true);
		}
	};

	// Handle scroll detection for infinite scroll
	const handleListboxScroll = event => {
		const listboxNode = event.currentTarget;
		const isNearBottom =
			listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10;

		if (isNearBottom && hasMore && !loading) {
			loadMore();
		}
	};

	// Create options with loading indicator if needed
	const optionsWithLoading = [...options];
	if (loading && hasMore && options.length > 0) {
		optionsWithLoading.push({ isLoading: true, code: "loading-indicator" });
	}

	/**
	 * Debounced search function to avoid excessive API calls
	 */
	const debouncedSearch = lod_.debounce(val => {
		search(val);
	}, 300);

	useEffect(() => {
		search("");
	}, [filter, page]);

	// Cleanup debounced function on unmount
	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	return (
		<MDBox key={filter.attribute} mr={1}>
			<Autocomplete
				sx={{
					flex: 1,
					width: "100%",
					minWidth: "10vw",
					"& .MuiAutocomplete-inputRoot": {
						padding: "0px !important"
					},
					"& .MuiAutocomplete-input": {
						padding: "12px !important",
						paddingLeft: "6px !important"
					}
				}}
				options={optionsWithLoading}
				onChange={onChangeAutocomplete}
				ListboxProps={{
					onScroll: handleListboxScroll,
					style: { maxHeight: "35vh" } // Set a max height to enable scrolling
				}}
				freeSolo
				defaultValue=""
				getOptionLabel={option => option[filter.attribute] || ""}
				onInputChange={(e, value, reason) => {
					debouncedSearch(value);
				}}
				renderInput={params => {
					params.InputProps.startAdornment = (
						<InputAdornment position="start" sx={{ ml: 1, mr: 0 }}>
							<Icon size="small">filter_alt</Icon>
						</InputAdornment>
					);
					return (
						<MDInput
							{...params}
							label={getLabel(filter.label, language)}
							type={filter.type}
							placeholder={`Filtrer ${getLabel(filter.label, language)} ...`}
						/>
					);
				}}
			/>
		</MDBox>
	);
};

export default InputString;
